import { ReactNode, useMemo } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material';

import { IMyLensesFittedCardsListProps } from './my-lenses-fitted-cards-list.models';
import {
  IConsumerLensesParams,
  ProductType,
  LensesBrandType,
} from '../../../../../graphql/lenses/models/get-lenses.models';
import { useStyles } from './my-lenses-fitted-cards-list.styles';
import { Card } from '../../../../common/card';
import { LensesCard } from './lenses-card';
import { SolutionsCard } from './solutions-card';
import { useDependentColors } from '../../../../../hooks/use-dependent-colors';
import { v2026PP13312 } from '../../../../../constants/veeva';

export const MyLensesFittedCardsList = ({ cards }: IMyLensesFittedCardsListProps): JSX.Element => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'lg'));

  const productsWithExtraTopPadding = useMemo(() => {
    if (isMobile || !cards.length) {
      return cards;
    }

    const rowSize = isTablet ? 2 : 3;
    const rows: IConsumerLensesParams[][] = [];

    for (let index = 0; index < cards.length; index += rowSize) {
      rows.push(cards.slice(index, index + rowSize));
    }

    const isTopLabelBrand = (card: IConsumerLensesParams): boolean => (
      card.brandId === LensesBrandType.aomax1dfa
      || card.brandId === LensesBrandType.aomax1dmffa);

    const topLabelCards = rows.filter(row => row.some(isTopLabelBrand));

    return cards.map((card, cardIndex) => {
      const rowIndex = Math.floor(cardIndex / rowSize);
      const currentRow = rows[rowIndex];
      const hasTopLabel = topLabelCards.includes(currentRow);
      const isExtraPadding = hasTopLabel && !isTopLabelBrand(card);

      return {
        ...card,
        ...(isExtraPadding ? { isExtraPadding: true } : {}),
      };
    });
  }, [isTablet, isMobile, cards]);

  const classes = useStyles();
  const { getDependentColorIndex } = useDependentColors();

  const hasKidProfiles = cards.some((card) => card.dependent);

  return (
    <Card className={classes.root}>
      <span className={classes.veeva}>{v2026PP13312}</span>
      <ul className={classes.cardsList}>
        {productsWithExtraTopPadding.map((card: IConsumerLensesParams, index) => {
          let fittedCard: ReactNode;
          const dependent = card.dependent;
          const profileColorIndex = dependent?.firstName && dependent?.lastName
            ? getDependentColorIndex(dependent.firstName, dependent.lastName)
            : undefined;

          if (card.brand) {
            switch (card.brand.productType) {
              case ProductType.lenses:
                fittedCard = (
                  <LensesCard
                    key={`${card.brandId}-${dependent?.firstName}-${dependent?.lastName}-${index}`}
                    cardData={card}
                    profileFirstName={
                      dependent?.firstName || (hasKidProfiles ? 'Consumer' : undefined)
                    }
                    profileLastName={dependent?.lastName}
                    profileIsActive={dependent?.active ?? true}
                    profileColorIndex={profileColorIndex}
                    hasKidProfiles={hasKidProfiles}
                    isExtraPadding={card.isExtraPadding}
                  />
                );
                break;
              case ProductType.solutions:
                fittedCard = (
                  <SolutionsCard
                    key={`${card.brandId}-${dependent?.firstName}-${dependent?.lastName}-${index}`}
                    cardData={card}
                  />
                );
                break;
              default:
                fittedCard = null;
                break;
            }
          }
          return fittedCard;
        })}
      </ul>
    </Card>
  );
};
